import { Box, Grid, Paper, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { useFeatureFlagsContext } from '../../contexts/featureFlagContext';
import { BrokerageCanvasserDirectoryFailureNotice } from '../BrokerageCanvasserDirectoryFailureNotice';
import { useBrokerageCanvasserDirectory } from '../BrokerageCanvasserDirectoryContext';
import { EmptyState } from '../EmptyState';
import { ErrorState } from '../ErrorState';
import { LoadingState } from '../LoadingState';
import { PageHeader } from '../PageHeader';
import { useDashboardAccountScope } from './voterAccountScope';
import type { VoterFetchImpl } from './voterApi';
import { VoterDoorVisitTable } from './VoterDoorVisitTable';
import { VoterLocationMap } from './VoterLocationMap';
import {
  buildDoorVisitLocationState,
  buildVoterDetailPath,
  readVotersListState,
  toStoredVotersListState,
} from './voterNavigation';
import {
  DEFAULT_VOTER_PAGE_SIZE,
  LOCATION_UNAVAILABLE,
  MISSING_FIELD,
  NO_ASSOCIATED_DOOR_VISITS,
  NO_HOUSEHOLD_LINKED,
  VOTER_ASSOCIATION_EXPLANATION,
  VOTER_UNAVAILABLE,
  displayField,
  displayVoterName,
  formatHouseholdAddress,
  hasDisplayableHouseholdCoordinates,
  isVoterPageSize,
} from './voterPresentation';
import { DEFAULT_VOTER_PAGE } from './voterListState';
import { useVoterDetail } from './useVoterDetail';
import { useVoterDoorVisits } from './useVoterDoorVisits';

const visuallyHiddenSx = {
  position: 'absolute',
  width: 1,
  height: 1,
  p: 0,
  m: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
} as const;

const identifierSx = { overflowWrap: 'anywhere', whiteSpace: 'normal', wordBreak: 'break-word' } as const;

export type VoterDetailPageProps = {
  fetchImpl?: VoterFetchImpl;
};

const Field = ({ label, value }: { label: string; value: string }) => (
  <Box>
    <Typography component="dt" variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography component="dd" variant="body1" sx={{ m: 0, ...identifierSx }}>
      {value}
    </Typography>
  </Box>
);

export const VoterDetailPage = ({ fetchImpl }: VoterDetailPageProps) => {
  const { id = '' } = useParams<{ id: string }>();
  const location = useLocation();
  const { isStructurelyAdmin } = useFeatureFlagsContext();
  const accountScope = useDashboardAccountScope();
  const { resolveLabel } = useBrokerageCanvasserDirectory();
  const listState = readVotersListState(location.state, accountScope);
  const [seenScope, setSeenScope] = useState(accountScope);
  const [visitPage, setVisitPage] = useState(DEFAULT_VOTER_PAGE);
  const [visitRowsPerPage, setVisitRowsPerPage] = useState(DEFAULT_VOTER_PAGE_SIZE);
  const [seenVoterId, setSeenVoterId] = useState(id);

  if (seenScope !== accountScope || seenVoterId !== id) {
    setSeenScope(accountScope);
    setSeenVoterId(id);
    setVisitPage(DEFAULT_VOTER_PAGE);
    setVisitRowsPerPage(DEFAULT_VOTER_PAGE_SIZE);
  }

  const detail = useVoterDetail({ id, fetchImpl });
  const visits = useVoterDoorVisits({
    id,
    page: seenScope === accountScope && seenVoterId === id ? visitPage : DEFAULT_VOTER_PAGE,
    rowsPerPage: seenScope === accountScope && seenVoterId === id ? visitRowsPerPage : DEFAULT_VOTER_PAGE_SIZE,
    fetchImpl,
  });
  const voter = detail.data?.voter ?? null;
  const detailPath = buildVoterDetailPath(id);
  const visitLinkState = detailPath === null ? undefined : buildDoorVisitLocationState(id, listState, accountScope);
  const backState = listState === null ? undefined : { votersList: toStoredVotersListState(listState, accountScope) };

  useEffect(() => {
    if (visits.data === null || visits.loading) return;
    const lastPage = Math.max(0, Math.ceil(visits.data.total / visitRowsPerPage) - 1);
    if (visits.data.total > 0 && visitPage > lastPage) setVisitPage(lastPage);
  }, [visitPage, visitRowsPerPage, visits.data, visits.loading]);

  const handleVisitRowsPerPage = (nextRowsPerPage: number): void => {
    if (!isVoterPageSize(nextRowsPerPage)) return;
    setVisitRowsPerPage(nextRowsPerPage);
    setVisitPage(DEFAULT_VOTER_PAGE);
  };

  const household = voter?.household ?? null;
  const addressLines = formatHouseholdAddress(household);
  const showMap = household !== null && hasDisplayableHouseholdCoordinates(household.lat, household.lng);
  const visitAnnouncement = visits.loading
    ? 'Loading door visits.'
    : visits.error !== null
      ? visits.error
      : visits.data === null || visits.data.total === 0
        ? NO_ASSOCIATED_DOOR_VISITS
        : `${visits.data.total} associated door visits.`;

  return (
    <>
      <PageHeader
        title={voter === null ? 'Voter' : displayVoterName(voter)}
        backLink={{ label: 'Voters', to: '/voters', ...(backState === undefined ? {} : { state: backState }) }}
      />
      <Box component="div" role="status" aria-live="polite" aria-atomic="true" sx={visuallyHiddenSx}>
        {detail.loading ? 'Loading voter.' : detail.error ?? (voter === null ? VOTER_UNAVAILABLE : displayVoterName(voter))}
      </Box>
      <Box component="div" role="status" aria-live="polite" aria-atomic="true" sx={visuallyHiddenSx}>
        {visitAnnouncement}
      </Box>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper variant="outlined" sx={{ p: 3, height: '100%' }}>
            <Typography component="h2" variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
              Voter
            </Typography>
            {detail.loading ? <LoadingState message="Loading voter." /> : null}
            {!detail.loading && detail.error !== null ? (
              <ErrorState message={detail.error} onRetry={detail.refetch} retryName="Retry voter details" />
            ) : null}
            {!detail.loading && detail.error === null && voter === null ? <EmptyState title={VOTER_UNAVAILABLE} /> : null}
            {!detail.loading && detail.error === null && voter !== null ? (
              <Stack component="dl" spacing={2} sx={{ m: 0 }}>
                <Field label="Name" value={displayVoterName(voter)} />
                {isStructurelyAdmin ? <Field label="Voter ID" value={displayField(voter.voterId)} /> : null}
                <Field label="State" value={displayField(voter.state)} />
                <Box>
                  <Typography component="dt" variant="caption" color="text.secondary">
                    Household
                  </Typography>
                  {household === null ? (
                    <Typography component="dd" variant="body1" sx={{ m: 0 }}>
                      {NO_HOUSEHOLD_LINKED}
                    </Typography>
                  ) : addressLines.length === 0 ? (
                    <Typography component="dd" variant="body1" sx={{ m: 0 }}>
                      {MISSING_FIELD}
                    </Typography>
                  ) : (
                    addressLines.map(line => (
                      <Typography key={line} component="dd" variant="body1" sx={{ m: 0, ...identifierSx }}>
                        {line}
                      </Typography>
                    ))
                  )}
                </Box>
              </Stack>
            ) : null}
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper variant="outlined" sx={{ p: 3, height: '100%' }}>
            <Typography component="h2" variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
              Location
            </Typography>
            {detail.loading ? <LoadingState message="Loading location." /> : null}
            {!detail.loading && !showMap ? <Typography>{LOCATION_UNAVAILABLE}</Typography> : null}
            {!detail.loading && showMap && household !== null ? (
              <VoterLocationMap latitude={household.lat} longitude={household.lng} />
            ) : null}
          </Paper>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography component="h2" variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Door visits
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {VOTER_ASSOCIATION_EXPLANATION}
            </Typography>
            <BrokerageCanvasserDirectoryFailureNotice />
            {visits.loading ? <LoadingState message="Loading door visits." /> : null}
            {!visits.loading && visits.error !== null ? (
              <ErrorState message={visits.error} onRetry={visits.refetch} retryName="Retry door visits" />
            ) : null}
            {!visits.loading && visits.error === null && (visits.data === null || visits.data.items.length === 0) ? (
              <EmptyState title={NO_ASSOCIATED_DOOR_VISITS} />
            ) : null}
            {!visits.loading && visits.error === null && visits.data !== null && visits.data.items.length > 0 ? (
              <VoterDoorVisitTable
                items={visits.data.items}
                total={visits.data.total}
                page={visitPage}
                rowsPerPage={visitRowsPerPage}
                visitLinkState={visitLinkState}
                resolveCanvasserLabel={resolveLabel}
                onPageChange={setVisitPage}
                onRowsPerPageChange={handleVisitRowsPerPage}
              />
            ) : null}
          </Paper>
        </Grid>
      </Grid>
    </>
  );
};
